import React, { FC } from 'react';

import { Controller } from 'react-hook-form';
import ReactSelect from 'react-select';

import Checkbox from 'components/common/ui-kit/Checkbox';
import ProfileSection from 'components/profile/ProfileSection';
import FillingStatementsForm from 'components/statements/FillingStatementsForm';
import useGovernmentContractForm from 'hooks/statements/government-contracts/useGovernmentContractForm';
import terms from 'i18n';
import { ApplicationState } from 'models/applications/enum';
import { selectIsUserAdmin } from 'redux/selectors';

import useAppSelector from '../../../hooks/common/useAppSelector';
import { useGetTargetOrganizations } from '../../../hooks/common/useGetTargetOrganizations';
import {
  selectApplication,
  selectIsApplicationReadonly,
} from '../../../redux/selectors/enrollee/application';
import { useIsImpersonate } from '../../../utils/domain/impersonate';
import {
  createInputV2Props,
  createSelectProps,
} from '../../../utils/form-helpers';
import Input, { InputV2 } from '../../common/ui-kit/Input/view';
import LazyFileUploader from '../../common/ui-kit/LazyFileUploader';

const GovernmentContractForm: FC = () => {
  const targetOrganizations = useGetTargetOrganizations();
  const isImpersonate = useIsImpersonate();
  const isAdmin = useAppSelector(selectIsUserAdmin);
  const application = useAppSelector(selectApplication);

  const readOnly =
    (useAppSelector(selectIsApplicationReadonly) && !isImpersonate) ||
    (!isAdmin && application?.state === ApplicationState.ENROLLED); // https://gitlab.tune-it.ru/polytech/abit/frontend/-/issues/750;;

  const {
    fields,
    control,
    onSubmit,
    fileProps,
    hasNotContract,
    rvrPublished,
    formState: { errors },
  } = useGovernmentContractForm();

  const selectProps = createSelectProps(errors, readOnly);

  const inputProps = createInputV2Props(errors, readOnly);

  return (
    <FillingStatementsForm
      onSubmit={e => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <Checkbox
        className="profile-form__checkbox--mb-24"
        text={terms.HAS_NO_CONTRACT}
        {...fields.hasNotContract}
        disabled={readOnly}
      />

      {!hasNotContract && (
        <>
          <ProfileSection title={terms.OFFER_DATA}>
            <div className="profile-form__fields">
              <Controller
                name="organizationId"
                control={control}
                render={({ field }) => (
                  <ReactSelect
                    isClearable
                    placeholder={terms.ORGANIZATION_NAME}
                    className="profile-form__field-without-mh profile-form__field--fb-100"
                    {...selectProps(field, {
                      options: targetOrganizations,
                    })}
                  />
                )}
              />

              <Input
                markRequired
                disabled={readOnly}
                placeholder={terms.OFFER_NUMBER}
                className="profile-form__field"
                {...fields.offerNumber}
                error={errors?.offerNumber?.message}
              />

              <Controller
                name={fields.dateOfIssue.name}
                control={control}
                render={({ field }) => (
                  <InputV2
                    type="date"
                    markRequired
                    placeholder={terms.ISSUE_DATE}
                    className="profile-form__field"
                    {...inputProps(field)}
                  />
                )}
              />
            </div>
          </ProfileSection>
          <Checkbox
            className="profile-form__input"
            text='Предложение размещено на "РвР"'
            disabled={true}
            checked={!!rvrPublished}
          />
          <ProfileSection title={terms.DOCUMENT_FILE} markRequired>
            <LazyFileUploader
              {...fileProps}
              className="profile-form__file-uploader"
            />
            {errors.wasDocumentAttached && (
              <div className="profile-form__error">
                {errors.wasDocumentAttached?.message}
              </div>
            )}
          </ProfileSection>
        </>
      )}
    </FillingStatementsForm>
  );
};

export default GovernmentContractForm;
